import { CLOSETGroupsSearchType } from 'types/api/groups/closet';
import { SelectOption } from 'types/common/select';

interface SearchTypeConfig {
  label: string;
  // [EN] The server-side column length. A longer keyword matches nothing, so the
  //      input caps here instead of accepting a query that must return zero rows.
  // [KO] 서버 컬럼 길이다. 이보다 길면 아무것도 매칭되지 않으므로, 반드시 0건이 되는
  //      검색어를 받지 않고 입력 단계에서 자른다.
  maxLength: number;
  placeholder: string;
}

// [EN] Every search now names exactly one column, so there is no unified option
//      to fall back on and the screen must open on some concrete type.
// [KO] 이제 모든 검색이 컬럼 하나를 반드시 지목하므로 돌아갈 통합 검색 항목이 없고,
//      화면은 구체적인 타입 하나로 열려야 한다.
export const DEFAULT_SEARCH_TYPE = CLOSETGroupsSearchType.GROUP_NAME;

export const searchTypeConfig: { [key in CLOSETGroupsSearchType]: SearchTypeConfig } = {
  [CLOSETGroupsSearchType.GROUP_ID]: {
    label: 'Group ID',
    maxLength: 10,
    placeholder: 'Group ID',
  },
  [CLOSETGroupsSearchType.GROUP_NAME]: {
    label: 'Group Name',
    maxLength: 30,
    placeholder: 'Group Name',
  },
  [CLOSETGroupsSearchType.EMAIL]: {
    label: 'Email',
    maxLength: 200,
    placeholder: 'Group admin Email',
  },
  [CLOSETGroupsSearchType.DOMAIN]: {
    label: 'Email Domain',
    maxLength: 64,
    placeholder: 'Group admin Email domain',
  },
};

// [EN] Derived from the config rather than listed again: the mapped type already
//      forces a new enum member into the config, so deriving makes it impossible to
//      add a type that never reaches the selector. Integer-like keys enumerate in
//      ascending order, which is the order the ticket lists.
// [KO] 목록을 다시 적지 않고 config 에서 파생시킨다. mapped type 이 새 enum 멤버의
//      config 등록을 이미 강제하므로, 파생시키면 셀렉터에만 빠지는 타입이 생길 수 없다.
//      정수형 키는 오름차순으로 열거되고 그것이 티켓의 나열 순서다.
export const searchTypeOptions: SelectOption[] = Object.keys(searchTypeConfig).map(key => {
  const value = Number(key) as CLOSETGroupsSearchType;
  return { value, label: searchTypeConfig[value].label };
});

// [EN] Anything this screen does not define — missing, empty, the server's `0`,
//      fractional, out of range — resolves to the default rather than erroring or
//      reviving the full scan. Links shared before CLOSET-17914 land here.
// [KO] 이 화면이 정의하지 않은 값(부재·빈 문자열·서버의 `0`·소수·범위 밖)은 모두
//      기본값으로 해석한다. 에러도, 풀스캔 부활도 아니다. CLOSET-17914 이전에 공유된
//      링크가 여기로 온다.
export const parseSearchType = (raw: string | null): CLOSETGroupsSearchType => {
  if (raw === null || raw.trim() === '') {
    return DEFAULT_SEARCH_TYPE;
  }
  const value = Number(raw);
  return Number.isInteger(value) && value in searchTypeConfig
    ? (value as CLOSETGroupsSearchType)
    : DEFAULT_SEARCH_TYPE;
};

// [EN] Applied when the type changes, because an input's `maxLength` only blocks
//      new typing — a longer keyword carried over from another type would stay
//      and silently return zero rows.
// [KO] 타입이 바뀔 때 적용한다. 입력의 `maxLength` 는 새로 치는 것만 막아서, 다른
//      타입에서 넘어온 긴 키워드는 그대로 남아 조용히 0건을 만들기 때문이다.
export const clampKeyword = (keyword: string, searchType: CLOSETGroupsSearchType): string =>
  keyword.slice(0, searchTypeConfig[searchType].maxLength);
